import * as Sentry from "@sentry/react";
import { supabaseIntegration } from "@supabase/sentry-js-integration";
import { SupabaseClient } from "@supabase/supabase-js";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import { getPWAStatus } from "./utils/pwaDetection";

// Disable Sentry in development mode and when a production build is served
// locally (e.g. `vite preview`), so local errors never reach Sentry.
const isLocalHost = ["localhost", "127.0.0.1"].includes(window.location.hostname);

if (import.meta.env.DEV || isLocalHost) {
  console.log("Sentry is disabled in local environments");
} else {
  const pwaStatus = getPWAStatus();

  Sentry.init({
    enabled: true,
    dsn: "https://8f69861d6917335fcd80bdbe8352b6d7@o4508811720851456.ingest.de.sentry.io/4508811723800656",
    integrations: [
      Sentry.browserTracingIntegration({
        shouldCreateSpanForRequest: (url) => {
          return !url.startsWith(`${import.meta.env.VITE_SUPABASE_URL}/rest`);
        },
      }),
      Sentry.replayIntegration({ maskAllText: false, maskAllInputs: true, blockAllMedia: false }),
      supabaseIntegration(SupabaseClient, Sentry, {
        tracing: true,
        breadcrumbs: true,
        errors: true,
      }),
    ],
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Session Replay
    replaysSessionSampleRate: 0.3, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    initialScope: {
      tags: {
        'app.mode': pwaStatus.isInstalled ? 'pwa' : 'browser',
        'app.display_mode': pwaStatus.displayMode,
        'app.platform': pwaStatus.platform,
      },
    },
  });

  // Track app usage
  Sentry.addBreadcrumb({
    message: 'App launched',
    category: 'app',
    level: 'info',
    data: {
      isPWA: pwaStatus.isInstalled,
      displayMode: pwaStatus.displayMode,
      platform: pwaStatus.platform,
    },
  });
}

// Register service worker for PWA
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js')
      .then(registration => {
        console.log('ServiceWorker registered:', registration);
      })
      .catch(error => {
        console.log('ServiceWorker registration failed:', error);
      });
  });
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
